// BUG_REPORTER — TEMPORARY FEATURE. Delete this folder per docs/BUG_REPORTER.md.
//
// The form. Built on components/Modal.tsx (fullScreen) so nothing about the
// overlay, the ESC key or the portal is reimplemented here.
//
// It doubles as the confirm-before-send step: the reporter is looking at the
// exact image that will be uploaded, and every piece of metadata that travels
// with it is shown as a read-only chip. Nothing leaves invisibly.
//
// D47 (owner, 2026-09-18): one optional row under the title — "Same problem as an
// earlier report?" — opens the picker only when clicked, so the form is as short
// as before for a new bug. The client had filed one ticket per invoice for what
// was one bug (prod 0044, 0052, 0053).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { AnnotationCanvas, type AnnotationHandle } from './AnnotationCanvas';
import {
  SEVERITY_LABEL,
  newSubmissionId,
  submitBugReport,
  type BugLinkCandidate,
  type BugSeverity,
  type BugReportContext,
} from '../../lib/bugReportApi';
import { LinkPicker } from './LinkPicker';
import type { Capture } from './useScreenshot';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  capture: Capture | null;
  captureError: string | null;
  /** Everything the widget collected about where the reporter was standing. */
  context: Omit<BugReportContext, 'submissionId' | 'title' | 'description' | 'severity' | 'sameAsId'>;
  onFiled: (ticketNumber: string) => void;
};

const SEVERITIES: BugSeverity[] = ['blocker', 'major', 'minor', 'cosmetic'];

export const BugReportModal: React.FC<Props> = ({ isOpen, onClose, capture, captureError, context, onFiled }) => {
  const annotationRef = useRef<AnnotationHandle>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<BugSeverity>('major');
  // D47: the earlier report this is the same problem as, if the reporter says so.
  const [sameAs, setSameAs] = useState<BugLinkCandidate | null>(null);
  const [picking, setPicking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One id per opening of the form. It is the idempotency key: a double-click,
  // or a retry after a dropped connection, resolves to the SAME report on the
  // server rather than filing a second one.
  const [submissionId, setSubmissionId] = useState(newSubmissionId);

  useEffect(() => {
    if (!isOpen) return;
    setTitle('');
    setDescription('');
    setSeverity('major');
    setSameAs(null);
    setPicking(false);
    setError(null);
    setSubmitting(false);
    setSubmissionId(newSubmissionId());
  }, [isOpen]);

  const chips = useMemo(
    () =>
      [
        ['Screen', context.pageLabel],
        ['Route', `${context.path}${context.search}`],
        context.entityType ? ['Record', `${context.entityType.replace(/_/g, ' ')} ${context.entityId}`] : null,
        ['Window', context.viewport],
      ].filter(Boolean) as Array<[string, string]>,
    [context]
  );

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Give the bug a short title so it can be found later.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const blob = capture ? await annotationRef.current?.toBlob() : null;
      const res = await submitBugReport(
        { ...context, submissionId, title: title.trim(), description: description.trim(), severity, sameAsId: sameAs?.id ?? '' },
        blob ?? null
      );
      onFiled(res.data.report.ticketNumber);
      onClose();
    } catch (err: any) {
      // The server's prose, which the central handler wrote for a person. The
      // client never string-matches it (invariant 6) — it only displays it.
      setError(err?.message || 'The report could not be sent. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    // Above every other popup, because it is reachable from on top of one now
    // (ClickUp 14yj85ny3xf), the same level the launcher sits at. Below the
    // session-expired blocker (z-[999]), which nothing may cover.
    <Modal
      isOpen={isOpen}
      title="Report a bug"
      onClose={onClose}
      fullScreen
      bodyClass="flex-1 min-h-0 flex flex-col"
      overlayZClass="z-[110]"
    >
      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0 p-4 sm:p-6 overflow-auto lg:overflow-hidden">
        {/* Left: the image that will actually be sent */}
        <div className="flex-1 min-h-[280px] lg:min-h-0 flex flex-col">
          {capture ? (
            <AnnotationCanvas
              ref={annotationRef}
              source={capture.canvas}
              focus={capture.focus}
              viewportsWide={capture.viewportsWide}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
              <div>
                <p className="text-sm font-medium text-slate-700">No screenshot was captured.</p>
                <p className="mt-1 text-xs text-slate-500">
                  {captureError || 'This screen could not be captured.'} You can still send the report — please
                  describe what you were doing below.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right: the words */}
        <div className="w-full lg:w-96 shrink-0 flex flex-col gap-4 lg:overflow-y-auto">
          <div>
            <label htmlFor="bug-title" className="block text-xs font-semibold text-slate-700 mb-1">
              What went wrong?
            </label>
            <input
              id="bug-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="e.g. Invoice total is $0.00"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>

          <div>
            <p id="bug-same-as" className="block text-xs font-semibold text-slate-700 mb-1">
              Same problem as an earlier report? <span className="font-normal text-slate-400">(optional)</span>
            </p>
            {sameAs ? (
              <div
                aria-labelledby="bug-same-as"
                role="group"
                className="flex items-start gap-2 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 text-slate-700">
                  <span className="font-medium text-slate-900">{sameAs.linkedToTicket || sameAs.ticketNumber}</span>
                  {sameAs.linkedToTicket ? null : <span className="break-words"> — {sameAs.title}</span>}
                  {sameAs.linkedToTicket ? (
                    <span className="block text-xs text-slate-500">
                      {sameAs.ticketNumber} is already linked to it, so this report is linked to {sameAs.linkedToTicket}.
                    </span>
                  ) : null}
                </span>
                <button type="button" onClick={() => setPicking(true)} className="text-xs font-medium text-slate-600 hover:underline">
                  Change
                </button>
                <button type="button" onClick={() => setSameAs(null)} className="text-xs font-medium text-slate-600 hover:underline">
                  Remove
                </button>
              </div>
            ) : !picking ? (
              <button
                type="button"
                aria-describedby="bug-same-as"
                onClick={() => setPicking(true)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                Choose a report…
              </button>
            ) : null}
            {picking ? (
              <div className="mt-2">
                <LinkPicker
                  screen={context.pageLabel}
                  onPick={(c) => {
                    setSameAs(c);
                    setPicking(false);
                  }}
                  onCancel={() => setPicking(false)}
                />
              </div>
            ) : null}
          </div>

          <div>
            <label htmlFor="bug-description" className="block text-xs font-semibold text-slate-700 mb-1">
              What were you doing? <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <textarea
              id="bug-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              maxLength={5000}
              placeholder="Steps you took, what you expected, what happened instead."
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-slate-400"
            />
          </div>

          <div>
            <label htmlFor="bug-severity" className="block text-xs font-semibold text-slate-700 mb-1">
              How bad is it?
            </label>
            <select
              id="bug-severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as BugSeverity)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-400"
            >
              {SEVERITIES.map((s) => (
                <option key={s} value={s}>
                  {SEVERITY_LABEL[s]}
                </option>
              ))}
            </select>
          </div>

          {/* Read-only, and shown rather than merely collected: this is exactly
              what travels with the report, so there are no surprises. */}
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Sent with your report</p>
            <dl className="space-y-1.5">
              {chips.map(([k, v]) => (
                <div key={k} className="flex gap-2 text-xs">
                  <dt className="w-16 shrink-0 text-slate-500">{k}</dt>
                  <dd className="text-slate-700 break-all">{v || '—'}</dd>
                </div>
              ))}
            </dl>
            {capture && (
              <p className="mt-2 text-xs text-slate-500">
                The screenshot above is sent as you see it. Use <strong>Black out</strong> to cover any figure you
                would rather not share — it is painted into the image before it is sent.
              </p>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {submitting ? 'Sending…' : 'Send report'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
